import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { handleError } from '../lib/errorHandler';
import {
  CachedUserProfile,
  fetchCachedUserProfile,
  invalidateCachedUserProfile,
  patchCachedUserProfile,
} from '../lib/userProfileCache';

export type UserProfile = CachedUserProfile;

const REPROMPT_DAYS = 7;

const TWITTER_HANDLE_REGEX = /^[A-Za-z0-9_]{1,15}$/;
export function normalizeTwitterHandle(input: string): string {
  return input.trim().replace(/^@+/, '').toLowerCase();
}

interface VerificationResult {
  success: boolean;
  error?: string;
  sandboxRequired?: boolean;
  remainingAttempts?: number;
  phoneNumber?: string;
  channel?: 'whatsapp' | 'sms' | 'bypass';
  bypassed?: boolean;
}

export function useUserProfile() {
  const { user, session, isDemoMode } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);

  const fetchProfile = useCallback(async (force = false) => {
    if (!user || isDemoMode) {
      setLoading(false);
      return;
    }

    try {
      if (force) {
        invalidateCachedUserProfile(user.id);
      }
      const data = await fetchCachedUserProfile(user.id);
      setProfile(data);
      setError(null);
    } catch (err) {
      handleError(err, { hook: 'useUserProfile', action: 'fetchProfile', metadata: { userId: user.id } });
      setError('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  }, [user, isDemoMode]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const sendVerificationCode = useCallback(async (phoneNumber: string, options?: { forceSms?: boolean }): Promise<VerificationResult> => {
    if (!user || isDemoMode || !session?.access_token) {
      return { success: false, error: 'Not authenticated' };
    }

    setVerificationLoading(true);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('send-phone-verification', {
        body: { phoneNumber, forceSms: !!options?.forceSms },
      });

      if (invokeError) {
        // supabase.functions.invoke wraps non-2xx responses in FunctionsHttpError
        // whose body lives on error.context (a Response). Surface the real
        // server message (e.g. cap reached, invalid number) instead of a generic.
        let serverError: string | undefined;
        let sandboxRequired: boolean | undefined;
        try {
          const ctx = (invokeError as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) serverError = String(body.error);
            if (body?.sandboxRequired) sandboxRequired = !!body.sandboxRequired;
          }
        } catch {
          // ignore parse failures, fall through to generic
        }
        if (!serverError && data?.error) serverError = String(data.error);
        handleError(invokeError, { hook: 'useUserProfile', action: 'sendVerificationCode', metadata: { userId: user.id } });
        return {
          success: false,
          error: serverError || 'Unable to send verification code. Please try again later or contact support.',
          sandboxRequired,
        };
      }

      if (data?.error) {
        return { 
          success: false, 
          error: data.error,
          sandboxRequired: data.sandboxRequired 
        };
      }

      // Update local profile with the phone number (unverified)
      if (data?.phoneNumber) {
        const patch = {
          phone_number: data.phoneNumber,
          phone_number_verified: false,
        };
        setProfile(prev => (prev ? { ...prev, ...patch } : null));
        patchCachedUserProfile(user.id, patch);
      }

      setVerificationSent(true);
      return {
        success: true,
        phoneNumber: data?.phoneNumber,
        channel: data?.channel,
        bypassed: !!data?.bypassed,
      };
    } catch (err) {
      handleError(err, { hook: 'useUserProfile', action: 'sendVerificationCode', metadata: { userId: user.id } });
      return { success: false, error: 'Failed to send verification code' };
    } finally {
      setVerificationLoading(false);
    }
  }, [user, session, isDemoMode]);

  const verifyPhoneCode = useCallback(async (code: string): Promise<VerificationResult> => {
    if (!user || isDemoMode || !session?.access_token) {
      return { success: false, error: 'Not authenticated' };
    }

    setVerificationLoading(true);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('verify-phone-code', {
        body: { code },
      });

      if (invokeError) {
        let serverError: string | undefined;
        let remainingAttempts: number | undefined;
        try {
          const ctx = (invokeError as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) serverError = String(body.error);
            if (typeof body?.remainingAttempts === 'number') remainingAttempts = body.remainingAttempts;
          }
        } catch {
          // ignore parse failures
        }
        handleError(invokeError, { hook: 'useUserProfile', action: 'verifyPhoneCode', metadata: { userId: user.id } });
        return { success: false, error: serverError || invokeError.message, remainingAttempts };
      }

      if (data?.error) {
        return { 
          success: false, 
          error: data.error,
          remainingAttempts: data.remainingAttempts
        };
      }

      // Update local profile to mark as verified
      const patch = {
        phone_number_verified: true,
        profile_completed_at: new Date().toISOString(),
      };
      setProfile(prev => (prev ? { ...prev, ...patch } : null));
      patchCachedUserProfile(user.id, patch);

      setVerificationSent(false);
      return { success: true, phoneNumber: data?.phoneNumber };
    } catch (err) {
      handleError(err, { hook: 'useUserProfile', action: 'verifyPhoneCode', metadata: { userId: user.id } });
      return { success: false, error: 'Failed to verify code' };
    } finally {
      setVerificationLoading(false);
    }
  }, [user, session, isDemoMode]);

  const updatePhoneNumber = useCallback(async (phoneNumber: string): Promise<{ success: boolean; error?: string }> => {
    if (!user || isDemoMode) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const { error: updateError } = await supabase
        .from('users')
        .update({ 
          phone_number: phoneNumber,
          profile_completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) {
        handleError(updateError, { hook: 'useUserProfile', action: 'updatePhoneNumber', metadata: { userId: user.id } });
        return { success: false, error: updateError.message };
      }

      // Update local state
      const patch = {
        phone_number: phoneNumber,
        profile_completed_at: new Date().toISOString(),
      };
      setProfile(prev => (prev ? { ...prev, ...patch } : null));
      patchCachedUserProfile(user.id, patch);

      return { success: true };
    } catch (err) {
      handleError(err, { hook: 'useUserProfile', action: 'updatePhoneNumber', metadata: { userId: user.id } });
      return { success: false, error: 'Failed to update phone number' };
    }
  }, [user, isDemoMode]);

  const markProfilePromptDismissed = useCallback(async (): Promise<void> => {
    if (!user || isDemoMode) return;

    try {
      // Track when user dismissed the prompt (allows re-prompting after 7 days)
      const now = new Date().toISOString();
      await supabase
        .from('users')
        .update({ 
          profile_prompt_dismissed_at: now,
          updated_at: now
        })
        .eq('id', user.id);

      setProfile(prev => (prev ? { ...prev, profile_prompt_dismissed_at: now } : null));
      patchCachedUserProfile(user.id, { profile_prompt_dismissed_at: now });
    } catch (err) {
      // Silent fail - not critical
      console.warn('Failed to mark profile prompt as dismissed:', err);
    }
  }, [user, isDemoMode]);

  const needsProfileCompletion = useCallback((): boolean => {
    if (!profile) return false;
    if (profile.phone_number) return false;
    if (!profile.profile_prompt_dismissed_at) return true;
    const dismissedAt = new Date(profile.profile_prompt_dismissed_at);
    const daysSinceDismissal = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceDismissal >= REPROMPT_DAYS;
  }, [profile]);

  const needsPhoneVerification = useCallback((): boolean => {
    if (!profile) return false;
    // User needs to verify phone if they have one and it's not verified yet.
    if (!profile.phone_number || profile.phone_number_verified) return false;
    // Honour dismissal cooldown so the modal can't pop in an infinite loop —
    // settings/profile screen still surfaces a "verify now" affordance.
    if (profile.profile_prompt_dismissed_at) {
      const dismissedAt = new Date(profile.profile_prompt_dismissed_at);
      const daysSinceDismissal = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissal < REPROMPT_DAYS) return false;
    }
    return true;
  }, [profile]);

  const clearPhoneNumber = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!user || isDemoMode) return { success: false, error: 'Not authenticated' };
    try {
      const now = new Date().toISOString();
      const { error: updateError } = await supabase
        .from('users')
        .update({
          phone_number: null,
          phone_number_verified: false,
          profile_prompt_dismissed_at: now,
          updated_at: now,
        })
        .eq('id', user.id);
      if (updateError) {
        handleError(updateError, { hook: 'useUserProfile', action: 'clearPhoneNumber', metadata: { userId: user.id } });
        return { success: false, error: updateError.message };
      }
      const patch = {
        phone_number: null,
        phone_number_verified: false,
        profile_prompt_dismissed_at: now,
      };
      setProfile(prev => (prev ? { ...prev, ...patch } : null));
      patchCachedUserProfile(user.id, patch);
      return { success: true };
    } catch (err) {
      handleError(err, { hook: 'useUserProfile', action: 'clearPhoneNumber', metadata: { userId: user.id } });
      return { success: false, error: 'Failed to remove phone number' };
    }
  }, [user, isDemoMode]);

  const updateTwitterHandle = useCallback(async (handle: string): Promise<{ success: boolean; error?: string }> => {
    if (!user || isDemoMode) return { success: false, error: 'Not authenticated' };
    const normalized = normalizeTwitterHandle(handle);
    if (!normalized) return { success: false, error: 'Please enter a handle' };
    if (!TWITTER_HANDLE_REGEX.test(normalized)) {
      return { success: false, error: 'Use 1–15 letters, numbers or underscores' };
    }
    try {
      const { error: updateError } = await supabase
        .from('users')
        .update({ twitter_handle: normalized, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (updateError) {
        handleError(updateError, { hook: 'useUserProfile', action: 'updateTwitterHandle', metadata: { userId: user.id } });
        return { success: false, error: updateError.message };
      }
      setProfile(prev => (prev ? { ...prev, twitter_handle: normalized } : null));
      patchCachedUserProfile(user.id, { twitter_handle: normalized });
      return { success: true };
    } catch (err) {
      handleError(err, { hook: 'useUserProfile', action: 'updateTwitterHandle', metadata: { userId: user.id } });
      return { success: false, error: 'Failed to update X/Twitter handle' };
    }
  }, [user, isDemoMode]);

  const markTwitterPromptDismissed = useCallback(async (): Promise<void> => {
    if (!user || isDemoMode) return;
    try {
      const now = new Date().toISOString();
      await supabase
        .from('users')
        .update({ twitter_prompt_dismissed_at: now, updated_at: now })
        .eq('id', user.id);
      setProfile(prev => (prev ? { ...prev, twitter_prompt_dismissed_at: now } : null));
      patchCachedUserProfile(user.id, { twitter_prompt_dismissed_at: now });
    } catch (err) {
      console.warn('Failed to mark twitter prompt as dismissed:', err);
    }
  }, [user, isDemoMode]);

  const needsTwitterCompletion = useCallback((): boolean => {
    if (!profile) return false;
    if (profile.twitter_handle) return false;
    if (!profile.twitter_prompt_dismissed_at) return true;
    const dismissedAt = new Date(profile.twitter_prompt_dismissed_at);
    const daysSince = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
    return daysSince >= REPROMPT_DAYS;
  }, [profile]);

  return {
    profile,
    loading,
    error,
    updatePhoneNumber,
    clearPhoneNumber,
    markProfilePromptDismissed,
    needsProfileCompletion,
    needsPhoneVerification,
    refetch: () => fetchProfile(true),
    sendVerificationCode,
    verifyPhoneCode,
    verificationSent,
    verificationLoading,
    updateTwitterHandle,
    markTwitterPromptDismissed,
    needsTwitterCompletion,
  };
}
